import {
  Component,
  inject,
  input,
  signal,
  computed,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { TabComponent } from '../../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../../components/tab-layout/tab-layout.component';
import {
  CarouselComponent,
  CarouselItem,
} from '../../../../components/carousel/carousel.component';
import { SelectComponent } from '../../../../components/select/select.component';
import { ConfigService } from '../../../../services/config.service';
import { environment } from '../../../../../environments/environment';

interface SectionDurationRow {
  Section: string;
  'Duration(Min)': string;
  Percentage: number;
}

interface SlideDurationRow {
  Slide: string;
  'Duration(Min)': string;
  Percentage: number;
  Url: string;
}

interface SectionDurationResponse {
  success: boolean;
  message: string;
  messageCode: string;
  data: {
    page: number;
    pageSize: number;
    rows: SectionDurationRow[];
    filters: Record<string, string[]>;
    schema: any[];
  };
}

interface SlideDurationResponse {
  success: boolean;
  message: string;
  messageCode: string;
  data: {
    page: number;
    pageSize: number;
    rows: SlideDurationRow[];
    filters: Record<string, string[]>;
    schema: any[];
  };
}

@Component({
  selector: 'app-versions-calls-charts-tab',
  imports: [
    TabLayoutComponent,
    CommonModule,
    CarouselComponent,
    SelectComponent,
  ],
  providers: [
    { provide: TabComponent, useExisting: VersionsCallsChartsTabComponent },
  ],
  templateUrl: './versions-calls-charts-tab.component.html',
  styleUrls: ['./versions-calls-charts-tab.component.scss'],
})
export class VersionsCallsChartsTabComponent
  extends TabComponent
  implements OnInit
{
  override name = this.dictionaryService.get('VersionsCallsCharts');
  productId = input.required<string>();

  @ViewChild('slideCarousel') slideCarousel?: CarouselComponent;

  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private baseUrl = environment.apiBaseUrl;

  // Section duration data
  sectionDurations = signal<SectionDurationRow[]>([]);
  availableFilters = signal<Record<string, string[]>>({});
  selectedTopFilters = signal<Record<string, string[]>>({});

  // Selected section tab
  selectedSection = signal<string | null>(null);

  // Slide duration data
  slideDurations = signal<SlideDurationRow[]>([]);
  slidesLoading = signal<boolean>(false);
  topFiltersLoading = signal<boolean>(false);

  // Base URL for slide images (CDN)
  private readonly imageBaseUrl = environment.baseUrl;
  private sectionRequestId = 0;
  private slideRequestId = 0;
  private topFilterRefreshId = 0;
  private hasLoadedAvailableFilters = false;

  topFilterEntries = computed(() => {
    return Object.entries(this.availableFilters()).filter(
      ([filterName]) => filterName !== 'Section',
    );
  });

  carouselLoading = computed(
    () => this.slidesLoading() || this.topFiltersLoading(),
  );

  // Computed: carousel items from slide durations
  carouselItems = computed<CarouselItem[]>(() => {
    const slideNoLabel = this.dictionaryService.get('SlideNo');
    return this.slideDurations().map((slide) => ({
      id: slide.Slide,
      imageUrl: slide.Url ? this.imageBaseUrl + slide.Url : '',
      title: `${slideNoLabel} ${slide.Slide}`,
      subtitle: `Duration: ${slide['Duration(Min)']} mins (${slide.Percentage}%)`,
    }));
  });

  // Compute section bars with selection state
  sectionBars = computed(() => {
    const sections = this.sectionDurations();
    if (sections.length === 0) return [];

    return sections.map((section) => ({
      ...section,
      widthPercent: section.Percentage,
      isSelected: this.selectedSection() === section.Section,
    }));
  });

  ngOnInit(): void {
    this.main();
  }

  public async main(): Promise<void> {
    this.loading.set(true);
    this.error = null;

    try {
      await this.fetchSectionDurations();
      this.initialized.set(true);
    } catch (err: any) {
      this.error = err?.message || 'Failed to load data';
    } finally {
      this.loading.set(false);
    }
  }

  private async fetchSectionDurations(): Promise<void> {
    const { from, to } = this.configService.getApiDateTimeRange();
    const endpoint = `/products/tables/section-durations/${this.productId()}`;
    const requestId = ++this.sectionRequestId;
    const shouldFetchAvailableFilters = !this.hasLoadedAvailableFilters;

    const response = await firstValueFrom(
      this.http.post<SectionDurationResponse>(this.baseUrl + endpoint, {
        from,
        to,
        getAvailableFilters: shouldFetchAvailableFilters ? 1 : 0,
        filters: this.buildTopFiltersPayload(),
      }),
    );

    if (requestId !== this.sectionRequestId) return;

    if (!response.success) {
      throw new Error(response.message);
    }

    if (shouldFetchAvailableFilters) {
      this.hasLoadedAvailableFilters = true;
      this.availableFilters.set(response.data.filters || {});
    }

    const rows = response.data.rows || [];
    this.sectionDurations.set(rows);

    if (rows.length === 0) {
      this.selectedSection.set(null);
      this.slideDurations.set([]);
      return;
    }

    const currentSelectedSection = this.selectedSection();
    const sectionStillAvailable =
      !!currentSelectedSection &&
      rows.some((row) => row.Section === currentSelectedSection);

    const nextSection = sectionStillAvailable
      ? currentSelectedSection!
      : rows[0].Section;

    this.selectedSection.set(nextSection);
    await this.fetchSlideDurations(nextSection);
  }

  onTopFilterChange(filterName: string, selectedValues: string[]): void {
    const sanitizedSelectedValues = this.sanitizeFilterValues(selectedValues);

    this.selectedTopFilters.update((filters) => {
      const nextFilters = { ...filters };

      if (sanitizedSelectedValues.length === 0) {
        delete nextFilters[filterName];
        return nextFilters;
      }

      nextFilters[filterName] = sanitizedSelectedValues;
      return nextFilters;
    });

    if (sanitizedSelectedValues.length === 0) {
      this.selectedSection.set(null);
      this.sectionDurations.set([]);
      this.slideDurations.set([]);
      return;
    }

    this.slideCarousel?.reset();
    void this.refreshWithTopFilters();
  }

  private async refreshWithTopFilters(): Promise<void> {
    const refreshId = ++this.topFilterRefreshId;
    this.topFiltersLoading.set(true);

    try {
      await this.fetchSectionDurations();
    } catch {
      // Keep current UI mounted during interactive filtering.
    } finally {
      if (refreshId === this.topFilterRefreshId) {
        this.topFiltersLoading.set(false);
      }
    }
  }

  private buildTopFiltersPayload(): Record<string, string[]> {
    const selectedFilters = this.selectedTopFilters();
    const appliedFilters: Record<string, string[]> = {};

    Object.entries(this.availableFilters()).forEach(([key, allValues]) => {
      if (key === 'Section') return;

      const sanitizedAllValues = this.sanitizeFilterValues(allValues);
      const checkedValues = this.sanitizeFilterValues(
        selectedFilters[key] ?? sanitizedAllValues,
      );

      if (checkedValues.length === 0 || sanitizedAllValues.length === 0) {
        return;
      }

      if (checkedValues.length < sanitizedAllValues.length) {
        appliedFilters[key] = checkedValues;
      }
    });

    return appliedFilters;
  }

  private buildSlideFiltersPayload(
    sectionName: string,
  ): Record<string, string[]> {
    const filters = this.buildTopFiltersPayload();
    const normalizedSectionName = sectionName.trim();

    if (!normalizedSectionName) {
      return filters;
    }

    return {
      ...filters,
      Section: [normalizedSectionName],
    };
  }

  private sanitizeFilterValues(values: string[]): string[] {
    return values
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  // Section tab selection
  selectSection(sectionName: string): void {
    this.selectedSection.set(sectionName);
    this.slideCarousel?.reset();
    void this.fetchSlideDurations(sectionName);
  }

  // Fetch slide durations for a section
  private async fetchSlideDurations(sectionName: string): Promise<void> {
    const { from, to } = this.configService.getApiDateTimeRange();
    const endpoint = `/products/tables/slide-durations/${this.productId()}`;
    const requestId = ++this.slideRequestId;

    this.slidesLoading.set(true);

    try {
      const response = await firstValueFrom(
        this.http.post<SlideDurationResponse>(this.baseUrl + endpoint, {
          from,
          to,
          getAvailableFilters: 0,
          filters: this.buildSlideFiltersPayload(sectionName),
        }),
      );

      if (requestId !== this.slideRequestId) return;

      if (response.success) {
        this.slideDurations.set(response.data.rows);
      } else {
        this.slideDurations.set([]);
      }
    } catch {
      if (requestId !== this.slideRequestId) return;
      this.slideDurations.set([]);
    } finally {
      if (requestId === this.slideRequestId) {
        this.slidesLoading.set(false);
      }
    }
  }
}
